import GuideLayout from './GuideLayout';

export default function GuideBurocrazia() {
  return (
    <GuideLayout
      slug="burocrazia-host-affitti-brevi"
      title="Burocrazia host e property manager affitti brevi 2026"
      breadcrumbLabel="Burocrazia host"
      description="La checklist completa degli adempimenti per essere host in regola in Italia nel 2026: CIN obbligatorio (Legge 213/2023), SCIA al SUAP, comunicazioni ISTAT mensili (Ross1000), schedine Alloggiati Web entro 24h, tassa di soggiorno, regole regionali."
      readingTime={11}
      faqs={faqs}
      howTo={howTo}
    >
      <h2>La mappa completa degli adempimenti</h2>
      <p>
        Essere host di affitti brevi in Italia significa rispondere a <strong>tre livelli di norma</strong> contemporaneamente: <strong>nazionale</strong> (legge 213/2023, codice del turismo, GDPR), <strong>regionale</strong> (codici identificativi locali — CIR, CIRT, ITALY01, CITRA), <strong>comunale</strong> (SCIA, tassa di soggiorno, regolamenti).
      </p>
      <p>
        Vediamo cosa serve, in ordine cronologico, da quando decidi di affittare a quando ricevi il primo ospite.
      </p>

      <h2>1. CIN — Codice Identificativo Nazionale</h2>
      <p>
        Introdotto dalla <strong>Legge 213/2023</strong> (decreto attuativo del 6 giugno 2024) e operativo dal <strong>1° gennaio 2025</strong>, il CIN è il codice univoco che identifica ogni unità destinata ad affitto breve in Italia. Sostituisce e ingloba i codici regionali pre-esistenti.
      </p>
      <ul>
        <li>Si ottiene su <strong>bdsr.ministeroturismo.gov.it</strong> (Banca Dati Strutture Ricettive del Ministero del Turismo).</li>
        <li>Richiede credenziali <strong>SPID/CIE</strong> e i dati catastali dell'immobile.</li>
        <li>Va <strong>esposto fisicamente all'esterno</strong> dell'immobile (targa o adesivo) e <strong>pubblicato in ogni annuncio</strong> online (Airbnb, Booking, Vrbo, sito proprio).</li>
        <li>Sanzioni: <strong>800€ - 8.000€</strong> per assenza in annuncio; <strong>500€ - 5.000€</strong> per mancata esposizione fisica.</li>
      </ul>

      <div className="callout">
        Dal 1° gennaio 2025 Airbnb, Booking e Vrbo sono <strong>obbligati a rifiutare</strong> annunci senza CIN. Se il tuo annuncio è ancora online vuol dire che hai già adempiuto.
      </div>

      <h2>2. SCIA al SUAP del Comune</h2>
      <p>
        La <strong>SCIA</strong> (Segnalazione Certificata di Inizio Attività) si presenta solo se l'attività è <strong>imprenditoriale</strong>. Per le locazioni turistiche pure (senza servizi) entro i limiti della cedolare (max 4 immobili) <strong>non serve SCIA</strong>.
      </p>
      <p>Serve invece nei seguenti casi:</p>
      <ul>
        <li><strong>Bed &amp; Breakfast</strong>: con o senza prima colazione, sempre attività professionale.</li>
        <li><strong>Casa Vacanze imprenditoriale</strong>: gestione di più unità con servizi accessori (pulizie, cambio biancheria periodico, check-in con personale).</li>
        <li><strong>Affittacamere</strong>: classico modello "stanze in casa propria con servizi".</li>
        <li><strong>5+ immobili</strong> in affitto breve: scatta presunzione di imprenditorialità per Legge 213/2023.</li>
      </ul>
      <p>
        La SCIA si presenta tramite il <strong>SUAP</strong> (Sportello Unico per le Attività Produttive) del Comune, su piattaforme tipo "impresainungiorno.gov.it". Richiede:
      </p>
      <ul>
        <li>Visura camerale (se ditta individuale, partita IVA aperta).</li>
        <li>Asseverazione tecnico abilitato (geometra/architetto) sulla conformità urbanistica.</li>
        <li>Notifica sanitaria ASL (per i B&amp;B che servono colazione).</li>
        <li>Eventuale autorizzazione antincendio (oltre 25 posti letto).</li>
      </ul>

      <h2>3. Codici regionali (pre-CIN)</h2>
      <p>
        Anche se è stato introdotto il CIN, in molte regioni rimane in vigore il <strong>codice regionale parallelo</strong> per finalità statistiche e ISTAT. Va comunque ottenuto/mantenuto fino a indicazione contraria.
      </p>
      <table>
        <thead>
          <tr><th>Regione</th><th>Codice</th><th>Portale</th></tr>
        </thead>
        <tbody>
          <tr><td>Lombardia</td><td>CIR</td><td>ricettivo.servizirl.it</td></tr>
          <tr><td>Lazio</td><td>CIR</td><td>turismolazio.it</td></tr>
          <tr><td>Toscana</td><td>CIN-IT-T</td><td>turismo.toscana.it</td></tr>
          <tr><td>Liguria</td><td>CITRA</td><td>regione.liguria.it</td></tr>
          <tr><td>Veneto</td><td>CIR</td><td>turismo.regione.veneto.it</td></tr>
          <tr><td>Emilia-Romagna</td><td>—</td><td>turismo.regione.emilia-romagna.it</td></tr>
          <tr><td>Campania</td><td>CIR</td><td>regione.campania.it/turismo</td></tr>
          <tr><td>Piemonte</td><td>CIR</td><td>regione.piemonte.it/turismo</td></tr>
        </tbody>
      </table>

      <h2>4. ISTAT Ross1000 — Rilevazione mensile</h2>
      <p>
        Il <strong>portale Ross1000</strong> (rilevazione "Movimento dei clienti negli esercizi ricettivi") richiede che ogni host comunichi <strong>mensilmente</strong>, entro il 5 del mese successivo, dati come:
      </p>
      <ul>
        <li>Numero di arrivi (clienti unici)</li>
        <li>Numero di presenze (notti totali)</li>
        <li>Paese di provenienza degli ospiti</li>
        <li>Tipologia di camera utilizzata</li>
      </ul>
      <p>
        I dati confluiscono nelle statistiche ufficiali italiane sul turismo. Sanzioni per omissione: <strong>500€ - 5.000€</strong> ex D.Lgs. 322/1989. Il portale di accesso varia per Regione (es. Liguria usa "Ross1000 Liguria", Lombardia ha un suo sistema integrato).
      </p>

      <h2>5. Alloggiati Web — Comunicazione alla Polizia entro 24h</h2>
      <p>
        Il <strong>portale Alloggiati Web</strong> (alloggiatiweb.poliziadistato.it) è il sistema con cui ogni host deve comunicare alla <strong>Questura competente</strong> le generalità degli ospiti entro <strong>24 ore</strong> dal check-in. Base normativa: <strong>art. 109 del TULPS</strong> (R.D. 773/1931) e <strong>D.M. 7 gennaio 2013</strong>.
      </p>
      <p>Per ogni ospite vanno trasmessi:</p>
      <ul>
        <li>Nome, cognome, data e luogo di nascita</li>
        <li>Cittadinanza</li>
        <li>Tipo e numero di documento (carta identità, passaporto, permesso di soggiorno)</li>
        <li>Data di arrivo (check-in)</li>
        <li>Durata del soggiorno</li>
      </ul>
      <p>
        Il portale rilascia una <strong>ricevuta in PDF</strong> ("schedina") che va conservata <strong>per 5 anni</strong>. Sanzioni: <strong>arresto fino a 3 mesi</strong> o ammenda fino a <strong>206€</strong> per ogni omissione. Sì, arresto. La Cassazione conferma sistematicamente la procedibilità d'ufficio.
      </p>

      <h2>6. Tassa di soggiorno</h2>
      <p>
        Disciplinata da <strong>art. 4 del D.Lgs. 23/2011</strong>, è introdotta dai Comuni con delibera. L'host è <strong>responsabile</strong> del pagamento dell'imposta (sostituto d'imposta del Comune) e deve:
      </p>
      <ul>
        <li>Riscuoterla dagli ospiti al check-in (con ricevuta separata o annotata sul contratto).</li>
        <li>Versarla al Comune con cadenza <strong>mensile o trimestrale</strong> a seconda del regolamento locale.</li>
        <li>Compilare la <strong>dichiarazione annuale</strong> entro il 30 giugno dell'anno successivo (ex art. 5-quinquies D.L. 146/2021).</li>
      </ul>
      <p>
        Importi tipici: da <strong>0,30€</strong> (piccoli comuni) a <strong>7,00€</strong> (Venezia centro storico, Roma, Milano centro), per notte e per persona, con esenzioni per minori (di norma sotto i 10-14 anni) e disabili.
      </p>

      <h2>7. Adempimenti GDPR per host</h2>
      <p>
        Quando raccogli copie di documenti, codici fiscali, indirizzi e dati di pagamento degli ospiti, tratti <strong>dati personali</strong> ex Reg. UE 2016/679. Obblighi minimi:
      </p>
      <ul>
        <li><strong>Informativa privacy</strong> al check-in (cartacea o digitale): finalità, base giuridica art. 6.1.b e c, durata conservazione.</li>
        <li><strong>Registro dei trattamenti</strong> Art. 30 (semplificato se gestisci 1-2 immobili).</li>
        <li><strong>Conservazione cifrata</strong> dei documenti caricati dagli ospiti (Locazione Turistica usa AES-128 at-rest).</li>
        <li><strong>Cancellazione automatica</strong> al termine del periodo di conservazione (es. 10 anni per fini fiscali, 5 anni per alloggiati web).</li>
      </ul>

      <h2>8. Adempimenti annuali</h2>
      <table>
        <thead>
          <tr><th>Scadenza</th><th>Adempimento</th></tr>
        </thead>
        <tbody>
          <tr><td>5 di ogni mese</td><td>ISTAT Ross1000 — comunicazione movimenti mese precedente</td></tr>
          <tr><td>16 marzo</td><td>CU OTA inviata all'Agenzia delle Entrate</td></tr>
          <tr><td>30 giugno</td><td>Saldo cedolare anno precedente + primo acconto</td></tr>
          <tr><td>30 giugno</td><td>Dichiarazione tassa di soggiorno (Comune)</td></tr>
          <tr><td>31 ottobre</td><td>Modello 730 / Redditi PF</td></tr>
          <tr><td>30 novembre</td><td>Secondo acconto cedolare</td></tr>
          <tr><td>Mensile/trimestrale</td><td>Versamento tassa soggiorno al Comune</td></tr>
        </tbody>
      </table>

      <h2>Differenze per tipologia struttura</h2>
      <table>
        <thead>
          <tr><th>Tipologia</th><th>SCIA</th><th>P.IVA</th><th>ATECO</th></tr>
        </thead>
        <tbody>
          <tr><td>Locazione turistica privata</td><td>NO</td><td>NO (sotto 5 unità)</td><td>—</td></tr>
          <tr><td>B&amp;B occasionale</td><td>SÌ</td><td>NO</td><td>55.20.51</td></tr>
          <tr><td>B&amp;B imprenditoriale</td><td>SÌ</td><td>SÌ</td><td>55.20.51 / 55.10</td></tr>
          <tr><td>Casa Vacanze (più unità)</td><td>SÌ</td><td>SÌ</td><td>55.20.52</td></tr>
          <tr><td>Affittacamere</td><td>SÌ</td><td>SÌ</td><td>55.20.51</td></tr>
        </tbody>
      </table>

      <blockquote>
        Affittare casa in regola non è impossibile — è solo noioso. Chi automatizza vince: 30 minuti di setup oggi ti risparmiano 200 ore di rincorse l'anno prossimo.
      </blockquote>
    </GuideLayout>
  );
}

const howTo = {
  name: 'Come mettersi in regola come host di affitti brevi in Italia',
  steps: [
    { name: 'Ottieni il CIN', text: 'Registrati su bdsr.ministeroturismo.gov.it con SPID/CIE, inserisci i dati catastali dell\'immobile e richiedi il Codice Identificativo Nazionale.' },
    { name: 'Verifica gli obblighi SCIA', text: 'Se gestisci B&B, casa vacanze imprenditoriale o 5+ immobili, presenta la SCIA al SUAP del Comune tramite "impresainungiorno.gov.it".' },
    { name: 'Richiedi i codici regionali', text: 'Verifica sul portale turismo della tua Regione (CIR, CITRA, CIN-IT-T) e ottieni il codice locale parallelo per fini ISTAT.' },
    { name: 'Iscriviti ad Alloggiati Web', text: 'Vai sulla Questura competente con SPID/CIE e richiedi le credenziali WSKEY per il portale alloggiatiweb.poliziadistato.it.' },
    { name: 'Configura tassa di soggiorno', text: 'Controlla il regolamento del Comune (cifre per notte, esenzioni, scadenze versamento) e configura il sistema di riscossione.' },
    { name: 'Implementa contratti e ricevute', text: 'Predisponi il contratto di locazione turistica con firma FES e il modello di ricevuta con marca da bollo automatica per importi > 77,47€.' },
    { name: 'Automatizza ISTAT e contabilità', text: 'Imposta promemoria mensili per ISTAT Ross1000 (entro il 5 del mese successivo) e tieni la contabilità con software dedicato per evitare errori sulla cedolare.' },
  ],
};

const faqs = [
  { q: 'Cos\'è il CIN e dove si richiede?', a: 'Il CIN (Codice Identificativo Nazionale) è il codice statale obbligatorio introdotto dalla Legge 213/2023 per ogni immobile in affitto breve. Si richiede sul portale del Ministero del Turismo bdsr.ministeroturismo.gov.it con SPID/CIE, inserendo i dati catastali dell\'immobile.' },
  { q: 'Devo presentare la SCIA per affittare casa su Airbnb?', a: 'Solo se l\'attività è imprenditoriale: B&B, casa vacanze imprenditoriale, affittacamere o 5+ immobili. Per una locazione turistica privata (max 4 immobili, senza servizi accessori) non serve SCIA.' },
  { q: 'Quanto tempo ho per comunicare gli ospiti ad Alloggiati Web?', a: 'Hai 24 ore dal check-in per trasmettere le generalità degli ospiti alla Questura competente tramite il portale alloggiatiweb.poliziadistato.it. La sanzione per omissione è severa: arresto fino a 3 mesi o ammenda fino a 206€ per ogni omissione, ex art. 109 TULPS.' },
  { q: 'Cos\'è la rilevazione ISTAT Ross1000?', a: 'È la comunicazione mensile dei movimenti dei clienti (arrivi e presenze) che ogni struttura ricettiva deve trasmettere entro il 5 del mese successivo. Il portale di accesso varia per Regione. Sanzioni per omissione: 500€-5.000€ ex D.Lgs. 322/1989.' },
  { q: 'Quanto costa la tassa di soggiorno?', a: 'Dipende dal Comune: va da 0,30€ a 7,00€ per notte/persona. Si applica solitamente fino a un massimo di 5-7 notti per ospite. Le esenzioni più comuni sono per minori (sotto 10-14 anni a seconda del Comune) e per persone con disabilità.' },
  { q: 'Cosa succede se gestisco 5 o più immobili in affitto breve?', a: 'Scatta la presunzione di imprenditorialità (Legge 213/2023): devi aprire partita IVA, presentare SCIA al SUAP, iscriverti in Camera di Commercio, scegliere regime fiscale (ordinario, semplificato o forfettario sotto 85k€ di ricavi). Esci automaticamente dalla cedolare secca.' },
  { q: 'Posso essere host senza partita IVA?', a: 'Sì, se gestisci fino a 4 immobili in modalità "locazione turistica" pura (senza servizi accessori obbligatori, senza pulizia giornaliera, senza prima colazione). Sopra i 4 immobili o con servizi alberghieri, scatta l\'obbligo di partita IVA.' },
];
